import * as React from "react";

import { MuiSkeleton } from "@/components/ui/mui-skeleton";

// The Item Search island's three loaders — the donor's
// `ItemSearchIsland/components/Skeleton.tsx`, ported once beside #798's other
// island faces (#811). All three are MUI `Skeleton`s at `animation='wave'`.

/**
 * `ItemCardSkeleton`: one card's place — rectangular, full width, `minHeight
 * 470` and `height 100%`, so it fills its grid row beside a taller card.
 */
export const ItemCardSkeleton = React.forwardRef<HTMLDivElement, { testId?: string }>(
  function ItemCardSkeleton({ testId = "item-card-skeleton" }, ref) {
    return (
      <MuiSkeleton
        ref={ref}
        data-testid={testId}
        variant="rectangular"
        animation="wave"
        className="h-full min-h-[470px] w-full"
      />
    );
  },
);

/**
 * `ItemGridSkeleton`: six cards, as grid items of whatever grid holds them —
 * a fragment in the donor, so they flow into the island's grid. The ref is the
 * first card's: the paging sentinel observes it. `asListItems` puts each in a
 * full-height `li`, for the card grid's list.
 */
export const ItemGridSkeleton = React.forwardRef<
  HTMLDivElement,
  { testId?: string; asListItems?: boolean }
>(function ItemGridSkeleton({ testId = "item-grid-skeleton", asListItems = false }, ref) {
  return (
    <>
      {[0, 1, 2, 3, 4, 5].map((index) => {
        const card = <ItemCardSkeleton ref={index === 0 ? ref : undefined} testId={testId} />;
        return asListItems ? (
          <li key={index} className="h-full" aria-hidden="true">
            {card}
          </li>
        ) : (
          <React.Fragment key={index}>{card}</React.Fragment>
        );
      })}
    </>
  );
});

/**
 * `IslandSkeleton`, the island's first load only: a column, gap 16, `px {xs
 * 32, sm 48, md 32, xl 48}`; the filter header's rounded bars — 56, 24 at 15%,
 * 56, and a fourth 56 below `xl` — in a column, gap 12, `pt 32 / pb 16`; then
 * six cards stacked in the column.
 */
export function IslandSkeleton() {
  return (
    <div
      data-testid="item-search-island-skeleton"
      className="flex flex-col gap-4 px-8 sm:px-12 md:px-8 xl:px-12"
    >
      <div className="flex w-full flex-col gap-3 pt-8 pb-4">
        <MuiSkeleton variant="rounded" animation="wave" className="h-14 w-full" />
        <MuiSkeleton variant="rounded" animation="wave" className="h-6 w-[15%]" />
        <MuiSkeleton variant="rounded" animation="wave" className="h-14 w-full" />
        <MuiSkeleton variant="rounded" animation="wave" className="h-14 w-full xl:hidden" />
      </div>
      <ItemGridSkeleton />
    </div>
  );
}
